import React, { useState, useEffect } from 'react';
import { bool, func, object, shape, string } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { injectIntl, intlShape } from '../../util/reactIntl';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { Page, LayoutSingleColumn } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import { parse } from '../../util/urlHelpers';
import { listingMatchesDeliveryOptions } from '../../util/listingDeliveryOptions';

import SearchFiltersBar from '../LandingPage/sections/SearchFiltersBar';
import SectionFeaturedListings from '../LandingPage/sections/SectionFeaturedListings';
import SearchPagination from '../LandingPage/sections/SearchPagination';
import { listingMatchesCondition } from '../../util/listingCondition';

import css from './SearchProductsPage.module.css';
import searchBackground from '../../assets/images/bailey-zindel-NRQV-hBF10M-unsplash.jpg';

const fetchSearchResults = (searchParams, setSearchResults, sortValue, filterValue, brandValue, dealerValue, sidebarKeyword, categoryValue) => (dispatch, getState, sdk) => {
  const searchQuery = {
    page: 1,
    perPage: 100,
    states: ['published'],
    include: ['author', 'author.profileImage', 'images'],
    minStock: 0,
    stockMode: 'match-undefined',
    'fields.listing': [
      'title', 'geolocation', 'price',
      'publicData.listingType', 'publicData.transactionProcessAlias', 'publicData.unitType',
      'publicData.pickupEnabled', 'publicData.shippingEnabled',
      'publicData.isAssociated', 'publicData.creatorId', 'publicData.creatorType',
      'publicData.deliveryPreference', 'publicData.shippingAddress', 'publicData.location',
      'publicData.storedAddress', 'publicData.category', 'publicData.subcategory',
      'publicData.videos', 'publicData.isManufacturer', 'publicData.hidden',
      'publicData.batchUpload', 'publicData.isTestListing', 'publicData.importData',
      'publicData.importSource', 'publicData.condition', 'publicData.brand', 'publicData.deliveryOption', 'publicData.isFeatured',
    ],
    'fields.user': ['profile.displayName', 'profile.abbreviatedName', 'profile.profileImage', 'profile.publicData'],
    'fields.image': ['variants.landscape-crop', 'variants.listing-card', 'variants.scaled-medium', 'variants.square-small'],
    'limit.images': 1,
  };

  if (sidebarKeyword) {
    searchQuery.keywords = sidebarKeyword;
  } else if (searchParams.keywords) {
    searchQuery.keywords = searchParams.keywords;
  }
  if (searchParams.pub_category) {
    searchQuery.pub_category = searchParams.pub_category;
  }
  if (searchParams.pub_subcategory) {
    searchQuery.pub_subcategory = searchParams.pub_subcategory;
  }
  if (categoryValue) {
    searchQuery.pub_category = categoryValue;
  }

  if (sortValue === 'newest') {
    searchQuery.sort = '-createdAt';
  } else if (sortValue === 'price-low') {
    searchQuery.sort = '-price,-createdAt';
  } else if (sortValue === 'price-high') {
    searchQuery.sort = 'price,-createdAt';
  } else if (sortValue === 'relevance') {
    searchQuery.sort = 'pub_rank,-createdAt';
  } else {
    searchQuery.sort = '-createdAt';
  }

  if (filterValue) {
    searchQuery.pub_condition = filterValue;
  }
  if (dealerValue && dealerValue !== 'all') {
    searchQuery.authorId = dealerValue;
  }

  const processListings = (allData, allIncluded) => {
    const includedUsers = allIncluded.filter(item => item.type === 'user');
    const approved = allData.filter(listing => {
      const authorId = listing.relationships?.author?.data?.id?.uuid;
      const author = includedUsers.find(user => user.id.uuid === authorId);
      return author?.attributes?.profile?.publicData?.approved_seller === true;
    });

    return approved.map(listing => {
      const authorId = listing.relationships?.author?.data?.id?.uuid;
      const author = allIncluded.find(item => item.type === 'user' && item.id.uuid === authorId);

      let authorWithProfileImage = author;
      if (author && author.relationships?.profileImage?.data?.id?.uuid) {
        const profileImageId = author.relationships.profileImage.data.id.uuid;
        const profileImage = allIncluded.find(item => item.type === 'image' && item.id.uuid === profileImageId);
        if (profileImage) {
          authorWithProfileImage = { ...author, profileImage };
        }
      }

      const imageIds = listing.relationships?.images?.data?.map(img => img.id.uuid) || [];
      const images = allIncluded.filter(item => item.type === 'image' && imageIds.includes(item.id.uuid));

      return { ...listing, author: authorWithProfileImage, images };
    });
  };

  return sdk.listings.query(searchQuery)
    .then(response => {
      if (!response.data || !response.data.data) return response;

      const totalPages = response.data.meta?.totalPages || 1;
      const remainingPages = [];
      for (let page = 2; page <= totalPages; page++) {
        remainingPages.push(sdk.listings.query({ ...searchQuery, page }));
      }

      return Promise.all(remainingPages).then(restResponses => {
        let allData = [...response.data.data];
        let allIncluded = [...(response.data.included || [])];

        restResponses.forEach(resp => {
          if (resp.data && resp.data.data) {
            allData = allData.concat(resp.data.data);
            allIncluded = allIncluded.concat(resp.data.included || []);
          }
        });

        setSearchResults(processListings(allData, allIncluded));
        return response;
      });
    })
    .catch(e => {
      console.error('Error fetching search results:', e);
      setSearchResults([]);
    });
};

export const SearchProductsPageComponent = props => {
  const { scrollingDisabled, intl, history, location, onFetchSearchResults } = props;

  const urlParams = parse(location.search);

  const [searchQuery, setSearchQuery] = useState(urlParams.keywords || '');
  const [searchResults, setSearchResults] = useState([]);
  const [sortValue, setSortValue] = useState('');
  const [filterValue, setFilterValue] = useState('');
  const [brandValue, setBrandValue] = useState('');
  const [deliveryValue, setDeliveryValue] = useState('');
  const [dealerValue, setDealerValue] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(
    () => typeof window !== 'undefined' && window.innerWidth >= 768
  );
  const [sidebarKeyword, setSidebarKeyword] = useState('');
  const [sellerTypeValue, setSellerTypeValue] = useState('');
  const [categoryValue, setCategoryValue] = useState(urlParams.pub_category || '');
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    onFetchSearchResults(urlParams, setSearchResults, sortValue, filterValue, brandValue, dealerValue, sidebarKeyword, categoryValue);
  }, [location.search, sortValue, filterValue, brandValue, dealerValue, sidebarKeyword, categoryValue]);

  useEffect(() => {
    setCategoryValue(urlParams.pub_category || '');
  }, [urlParams.pub_category]);

  useEffect(() => {
    setCurrentPage(1);
  }, [sortValue, filterValue, sellerTypeValue, categoryValue, sidebarKeyword, location.search]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      history.push(`/search-products?keywords=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  const heroStyle = {
    backgroundImage: `linear-gradient(0deg, rgba(30, 30, 30, 0.70) 0%, rgba(30, 30, 30, 0.70) 100%), url(${searchBackground})`
  };

  const title = intl.formatMessage({ id: 'SearchProductsPage.title' });
  const schemaTitle = intl.formatMessage({ id: 'SearchProductsPage.schemaTitle' });

  // Client-side filtering
  let displayListings = searchResults;

  if (sellerTypeValue && displayListings) {
    const selectedSellerTypes = sellerTypeValue.split(',');
    displayListings = displayListings.filter(listing => {
      const authorSellerType = listing?.author?.attributes?.profile?.publicData?.sellerType;
      return selectedSellerTypes.includes(authorSellerType);
    });
  }

  if (brandValue && displayListings) {
    const selectedBrands = brandValue.split(',');
    displayListings = displayListings.filter(listing =>
      selectedBrands.includes(listing?.attributes?.publicData?.brand)
    );
  }

  if (deliveryValue && displayListings) {
    const selectedDelivery = deliveryValue.split(',');
    displayListings = displayListings.filter(listing => {
      const publicData = listing?.attributes?.publicData || {};
      return listingMatchesDeliveryOptions(publicData, selectedDelivery);
    });
  }

  if (filterValue && displayListings) {
    const selectedConditions = filterValue.split(',');
    displayListings = displayListings.filter(listing => {
      const publicData = listing?.attributes?.publicData || {};
      return listingMatchesCondition(publicData, selectedConditions);
    });
  }

  const ITEMS_PER_PAGE = 20;
  const totalPages = Math.max(1, Math.ceil(displayListings.length / ITEMS_PER_PAGE));
  const safePage = Math.min(currentPage, totalPages);
  const pagedListings = displayListings.slice((safePage - 1) * ITEMS_PER_PAGE, safePage * ITEMS_PER_PAGE);

  const availableBrands = React.useMemo(() => {
    if (!searchResults || searchResults.length === 0) return [];
    return searchResults
      .map(l => l?.attributes?.publicData?.brand)
      .filter(Boolean)
      .filter((brand, index, self) => self.indexOf(brand) === index)
      .sort();
  }, [searchResults]);

  const availableDealers = React.useMemo(() => {
    if (!searchResults || searchResults.length === 0) return [];
    return searchResults
      .map(l => ({
        id: l?.author?.id?.uuid,
        name: l?.author?.attributes?.profile?.displayName || l?.author?.attributes?.profile?.abbreviatedName || 'Unknown'
      }))
      .filter(d => d.id)
      .filter((dealer, index, self) => self.findIndex(d => d.id === dealer.id) === index)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [searchResults]);

  const CATEGORY_LABELS = { archery: 'ARCHERY', fishing: 'FISHING', hunting: 'HUNTING/OUTDOORS' };
  const CONDITION_LABELS = { New: 'NEW', Used: 'USED', 'Open Box': 'OPEN BOX' };
  const SELLER_LABELS = { manufacturer: 'MANUFACTURERS', owner: 'DEALER', innovator: 'INNOVATOR', insider: 'INSIDER', independent: 'PEER' };

  const activeFilterParts = [];
  if (categoryValue) {
    activeFilterParts.push(...categoryValue.split(',').map(v => CATEGORY_LABELS[v] || v.toUpperCase()));
  } else if (urlParams.pub_category) {
    activeFilterParts.push(CATEGORY_LABELS[urlParams.pub_category] || urlParams.pub_category.toUpperCase());
  }
  if (filterValue) {
    activeFilterParts.push(...filterValue.split(',').map(v => CONDITION_LABELS[v] || v.toUpperCase()));
  }
  if (sellerTypeValue) {
    activeFilterParts.push(...sellerTypeValue.split(',').map(v => SELLER_LABELS[v] || v.toUpperCase()));
  }
  if (sidebarKeyword || urlParams.keywords) {
    activeFilterParts.push(`"${sidebarKeyword || urlParams.keywords}"`);
  }

  const handleClearFilters = () => {
    setSortValue('');
    setFilterValue('');
    setBrandValue('');
    setDeliveryValue('');
    setDealerValue('');
    setSidebarKeyword('');
    setSellerTypeValue('');
    setCategoryValue('');
    setCurrentPage(1);
    history.push('/search-products');
  };

  return (
    <Page title={title} scrollingDisabled={scrollingDisabled} schema={{ '@type': 'WebPage', name: schemaTitle }}>
      <LayoutSingleColumn>
        <TopbarContainer currentPage="SearchProductsPage" />

        {/* Banner */}
        <div className={css.banner} style={heroStyle}>
          <div className={css.content}>
            <h1 className={css.heading}>
              {intl.formatMessage({ id: 'SearchProductsPage.heading' })}
            </h1>
            <p className={css.subtitle}>
              {intl.formatMessage({ id: 'SearchProductsPage.subtitle' })}
            </p>
            <form className={css.searchForm} onSubmit={handleSearch}>
              <div className={css.searchInputWrapper}>
                <input
                  type="text"
                  className={css.searchInput}
                  placeholder={intl.formatMessage({ id: 'SearchProductsPage.placeholder' })}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <svg
                  className={css.searchIcon}
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M9 17A8 8 0 1 0 9 1a8 8 0 0 0 0 16zM19 19l-4.35-4.35"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <button type="submit" className={css.searchButton}>
                {intl.formatMessage({ id: 'SearchProductsPage.searchButton' })}
              </button>
            </form>
          </div>
        </div>

        {/* Search content — mirrors /s page search mode */}
        {activeFilterParts.length > 0 && (
          <p className={css.activeFilterLabel}>{activeFilterParts.join(' > ')}</p>
        )}

        {sidebarOpen && (
          <div className={css.mobileFilterOverlay} onClick={() => setSidebarOpen(false)}>
            <div className={css.mobileFilterPanel} onClick={e => e.stopPropagation()}>
              <div className={css.mobileFilterHeader}>
                <span className={css.mobileFilterTitle}>Filter &amp; Sort</span>
                <button className={css.mobileFilterClose} onClick={() => setSidebarOpen(false)}>
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="#1E1E1E" strokeWidth="2" strokeLinecap="round">
                    <line x1="1" y1="1" x2="13" y2="13" />
                    <line x1="13" y1="1" x2="1" y2="13" />
                  </svg>
                </button>
              </div>
              <div className={css.mobileFilterContent}>
                <SearchFiltersBar
                  isMobile
                  sortValue={sortValue}
                  onSortChange={setSortValue}
                  filterValue={filterValue}
                  onFilterChange={setFilterValue}
                  brandValue={brandValue}
                  onBrandChange={setBrandValue}
                  deliveryValue={deliveryValue}
                  onDeliveryChange={setDeliveryValue}
                  dealerValue={dealerValue}
                  onDealerChange={setDealerValue}
                  onClearFilters={handleClearFilters}
                  onKeywordSearch={setSidebarKeyword}
                  sellerTypeValue={sellerTypeValue}
                  onSellerTypeChange={setSellerTypeValue}
                  categoryValue={categoryValue}
                  onCategoryChange={setCategoryValue}
                  availableBrands={availableBrands}
                  availableDealers={availableDealers}
                />
              </div>
              <div className={css.mobileFilterFooter}>
                <button className={css.mobileFilterApply} onClick={() => setSidebarOpen(false)}>
                  Apply Filters
                </button>
              </div>
            </div>
          </div>
        )}

        <div className={css.searchLayout}>
          {sidebarOpen && (
            <div className={css.desktopSidebar}>
              <SearchFiltersBar
                sortValue={sortValue}
                onSortChange={setSortValue}
                filterValue={filterValue}
                onFilterChange={setFilterValue}
                brandValue={brandValue}
                onBrandChange={setBrandValue}
                dealerValue={dealerValue}
                onDealerChange={setDealerValue}
                onClearFilters={handleClearFilters}
                onKeywordSearch={setSidebarKeyword}
                sellerTypeValue={sellerTypeValue}
                onSellerTypeChange={setSellerTypeValue}
                categoryValue={categoryValue}
                onCategoryChange={setCategoryValue}
                availableBrands={availableBrands}
                availableDealers={availableDealers}
              />
            </div>
          )}
          <div className={css.searchMainContent}>
            <div className={css.searchHeaderRow}>
              <h2 className={css.searchTitle}>
                {sortValue || filterValue || sellerTypeValue || categoryValue || sidebarKeyword || urlParams.keywords || urlParams.pub_category || urlParams.pub_subcategory
                  ? 'Search results'
                  : 'All Products'}
              </h2>
              <button
                className={sidebarOpen ? `${css.filterSortButton} ${css.filterSortButtonActive}` : css.filterSortButton}
                onClick={() => setSidebarOpen(prev => !prev)}
              >
                {sidebarOpen ? 'Close filters' : 'Filter & Sort'}
              </button>
            </div>
            <SectionFeaturedListings
              listings={pagedListings}
              isSearchMode={true}
            />
            <SearchPagination
              currentPage={safePage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </div>
        </div>

        <FooterContainer />
      </LayoutSingleColumn>
    </Page>
  );
};

SearchProductsPageComponent.defaultProps = {
  scrollingDisabled: false,
};

SearchProductsPageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  intl: intlShape.isRequired,
  onFetchSearchResults: func.isRequired,
  location: shape({ search: string.isRequired }).isRequired,
  history: shape({ push: func.isRequired }).isRequired,
};

const mapStateToProps = state => {
  return {
    scrollingDisabled: isScrollingDisabled(state),
  };
};

const mapDispatchToProps = dispatch => ({
  onFetchSearchResults: (searchParams, setSearchResults, sortValue, filterValue, brandValue, dealerValue, sidebarKeyword, categoryValue) =>
    dispatch(fetchSearchResults(searchParams, setSearchResults, sortValue, filterValue, brandValue, dealerValue, sidebarKeyword, categoryValue)),
});

const SearchProductsPage = compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl
)(SearchProductsPageComponent);

export default SearchProductsPage;
